import { FC, useState, useRef } from "react";
import styled from "styled-components";
import { useLoaderData } from "react-router-dom";
import MainContainer from "../components/ui_components/MainContainer";
import { apiListArenasByInputSearch, apiListArenasByPage } from "../components/ui_components/ApiList";
import BoxPage from "../components/ppv_components/BoxPage";
import SingleCardArena from "../components/arena_components/SingleCardArena";
import { LoadingState } from "../components/stats_components/LoadingState";
import type { Arena, PaginatedResponse } from "../types";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 85%;
  margin: 24px auto 20px;
  gap: 16px;
`;

const SearchInput = styled.input`
  height: 40px;
  width: 260px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 0 14px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  transition: border-color 0.2s ease, background 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: rgba(163, 27, 27, 0.7);
    background: rgba(255, 255, 255, 0.07);
  }
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  width: 85%;
  margin: 0 auto 32px;
`;

const EmptyState = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  font-size: 14px;
  font-style: italic;
  padding: 40px 0;
  width: 100%;
`;

const ListArenas: FC = () => {
  const loaderData = useLoaderData() as PaginatedResponse<Arena>;
  const [responseData, setResponseData] = useState<PaginatedResponse<Arena>>(loaderData);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleSearchInput = (value: string): void => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (value.length === 0) { updateListArenaWithSearchBar(value); return; }
    if (value.length < 3) return;
    searchTimeoutRef.current = setTimeout(() => updateListArenaWithSearchBar(value), 800);
  };

  const updateListArenaWithSearchBar = async (value: string): Promise<void> => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
    try {
      setIsLoading(true);
      const response = await apiListArenasByInputSearch(value);
      if (!signal.aborted) setResponseData(response);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') console.error(error);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  const updateDataByPage = async (page: number): Promise<void> => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
    try {
      setIsLoading(true);
      const response = await apiListArenasByPage(page);
      if (!signal.aborted) setResponseData(response.data as unknown as PaginatedResponse<Arena>);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') console.error(error);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  if (isLoading) return <LoadingState />;

  return (
    <MainContainer>
      <Header>
        <SearchInput
          type="text"
          placeholder="Cerca arena (min. 3 caratteri)"
          value={searchInput}
          onChange={(e) => handleSearchInput(e.target.value)}
        />
        <BoxPage
          maxPageApi={responseData.last_page}
          currentPage={responseData.current_page}
          onPageChange={updateDataByPage}
        />
      </Header>

      <Grid>
        {responseData.data.length > 0 ? (
          responseData.data.map((arena, index) => (
            <SingleCardArena
              key={'arena-' + index + '-' + arena.id}
              arena={arena}
            />
          ))
        ) : (
          <EmptyState>Nessuna arena trovata</EmptyState>
        )}
      </Grid>
    </MainContainer>
  );
};

export default ListArenas;

export async function loader() {
  const response = await apiListArenasByPage(1);
  return response.data as unknown as PaginatedResponse<Arena>;
}
